import { EnumDialogName } from '@calm/evexia-analytics';
import { SxProps, Theme } from '@rhg/libs/material';
import SimpleDialog, {
    SimpleDialogProps,
} from '@rhg/libs/material/simple-dialog';
import {
    IconColor,
    DialogButton,
    BodyTextAlign,
} from '@rhg/libs/material/simple-dialog/types';
import {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useState,
} from 'react';

export const DEFAULT_TIMEOUT = 2000;
export type ToastDialogButton = Omit<DialogButton, 'onClick'>;

export interface ToastDialogState {
    toast: (options: ToastDialogOptions) => void;
    isOpen: boolean;
}
export const ToastDialogContext = createContext<ToastDialogState>(null!);

export interface ToastDialogOptions {
    trackDialog?: EnumDialogName;
    body?: string;
    bodyTextAlign?: BodyTextAlign;
    content?: ReactElement | ReactElement[] | null;
    title?: string;
    icon?: ReactElement;
    fullScreen?: boolean;
    hasGradientBackground?: boolean;
    iconColor?: IconColor;
    sx?: SxProps<Theme>;
    SimpleDialogProps?: Partial<
        Omit<SimpleDialogProps, 'iconContainerOverride'>
    >;
    timeout?: number;
}

interface Props {
    children: ReactNode;
}
export const ToastDialogProvider = ({ children }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<ToastDialogOptions | undefined>(
        undefined
    );

    const onClose = useCallback(() => {
        setIsOpen(false);
        setOptions(undefined);
    }, []);

    const toast = useCallback(
        (options?: ToastDialogOptions) => {
            setOptions(options);
            setIsOpen(true);
            setTimeout(onClose, options?.timeout ?? DEFAULT_TIMEOUT);
        },
        [onClose]
    );

    return (
        <>
            <ToastDialogContext.Provider value={{ toast, isOpen }}>
                {children}
            </ToastDialogContext.Provider>
            <SimpleDialog
                icon={options?.icon}
                iconColor={options?.iconColor}
                trackDialog={options?.trackDialog || EnumDialogName.Toast}
                buttons={[]}
                hideCloseButton={true}
                disableMobileFullscreen={true}
                dialogMaxWidth="md"
                fullWidth
                isOpen={isOpen}
                title={options?.title}
                onClose={onClose}
                bodyText={options?.body}
                bodyTextAlign={options?.bodyTextAlign}
                content={options?.content}
                {...options?.SimpleDialogProps}
            />
        </>
    );
};
